import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle, isTouchDevice } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean, requiredExtraWidth?: number }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * TS - Custom override (TS-493): layout-viewport width at which the inline menu is
   * known to fit again (collapse-time width + the reflow's reported deficit), replacing
   * the width-at-hide. Retrying at this width succeeds by construction, so the menu is
   * never speculatively rendered at widths where it still overflows (visible flicker).
   * @type {number | null}
   */
  #menuRecoveryWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * TS - Custom override (TS-563): document-space bottom of the header's
   * natural slot (scrollTop + rect.bottom), cached on every read where the
   * header isn't pinned. Content above the header (e.g. an enabled
   * announcement bar) shifts the slot down, so it can't be assumed to start
   * at y=0. 0 until first measured.
   * @type {number}
   */
  #naturalSlotBottom = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * TS - Custom override (TS-504): end of the window during which scroll events
   * are treated as scroll-anchoring artifacts of an animating <details>, not
   * user scrolling.
   * @type {number}
   */
  #anchorSuppressedUntil = 0;

  /**
   * TS - Custom override (TS-504): an opening/closing <details> animates its
   * content's block-size (accordion-custom), and when that re-centers
   * surrounding layout the browser's scroll anchoring compensates with scroll
   * events indistinguishable from user scroll-up. Open a suppression window
   * slightly longer than the animation so #updateScrollState ignores them.
   * `toggle` doesn't bubble, so this listens during capture.
   * @param {Event} event
   */
  #handleDetailsToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    this.#anchorSuppressedUntil = performance.now() + 600;
  };

  /**
   * TS - Custom override (TS-504): real scroll intent (wheel/touch/keys)
   * overrides the suppression window so a genuine scroll-up still reveals the
   * sticky header immediately, even mid-animation.
   *
   * TS - Custom override (TS-516): only keys that can scroll the page count.
   * Escape-closing a dialog dispatches dialog:close mid-keydown-propagation
   * (closeDialog resumes on a microtask between listeners), so this handler
   * runs AFTER the close set its suppression window — treating Escape as
   * scroll intent would clear that window and let the restore-scroll artifact
   * hide an active header.
   * @param {Event} event
   */
  #handleScrollIntent = (event) => {
    if (event instanceof KeyboardEvent && !HeaderComponent.#SCROLL_KEYS.has(event.key)) return;
    this.#anchorSuppressedUntil = 0;
  };

  /**
   * TS - Custom override (TS-516): the keys that scroll the document.
   * @type {Set<string>}
   */
  static #SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

  /**
   * TS - Custom override (TS-563): rest tolerance in px. Must cover the
   * sticky pin depth (`top: -1px` on .header-section in sections/header.liquid)
   * so a header stuck at the pin still counts as at-top — keep in sync if the
   * pin changes. Also absorbs sub-pixel momentum-settle jitter.
   * @type {number}
   */
  static #REST_TOLERANCE_PX = 1;

  /**
   * TS - Custom override (TS-577): downward travel (px) a scroll-up-revealed
   * header tolerates before hiding. iOS 26 momentum-settle jitter emits small
   * spurious down-reads at rest; with only the 1px rest tolerance those flip
   * 'active' → 'idle' (and the next up-read flips it back), so the header
   * flickers under the user's finger and taps on its buttons never land.
   * Genuine scroll-down accumulates past this within a single gesture (the
   * baseline stays frozen while holding), so the hide still feels immediate.
   * @type {number}
   */
  static #HIDE_HYSTERESIS_PX = 64;

  /**
   * TS - Custom override (TS-577): how long the sticky pin is held after the
   * fade-out starts — must cover the .header opacity transition
   * (--animation-speed, 125ms).
   * @type {number}
   */
  static #HIDE_FADE_MS = 200;

  /**
   * TS - Custom override (TS-516): closing a dialog-component releases its
   * scroll lock and restores the position with an instant scrollTo — a scroll
   * event that reads as user scrolling but runs after the lock-state check in
   * #updateScrollState stops applying (the open-side artifact is caught there
   * instead: it can arrive later than any timing window when the modal's
   * content injection blocks the next paint). Suppress briefly around both
   * events; dialog:open/close don't bubble, so listen during capture.
   */
  #handleDialogToggle = () => {
    this.#anchorSuppressedUntil = performance.now() + 300;
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    // TS - Custom override (TS-493): only once the layout viewport reaches the width
    // where the reflow said every link fits — not on any growth — so the desktop
    // layout is never rendered speculatively at widths where it would re-collapse.
    if (this.#menuRecoveryWidth && document.documentElement.clientWidth >= this.#menuRecoveryWidth) {
      this.#scheduleMenuRetry();
    }
  });

  /**
   * TS - Custom override (TS-493)
   * Debounce timer for re-showing the inline menu after the window grows.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #menuRetryTimeout = null;

  /**
   * TS - Custom override (TS-493)
   * Schedules a single menu retry once resizing has settled.
   */
  #scheduleMenuRetry() {
    if (this.#menuRetryTimeout) clearTimeout(this.#menuRetryTimeout);
    this.#menuRetryTimeout = setTimeout(() => {
      this.#menuRetryTimeout = null;
      if (this.#menuRecoveryWidth && document.documentElement.clientWidth >= this.#menuRecoveryWidth) {
        this.#updateMenuVisibility(false);
      }
    }, 150);
  }

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        // TS - Custom override (TS-577): 'hiding' is a pinned state like 'active'.
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === 'active' || this.dataset.stickyState === 'hiding';

        // TS - Custom override (TS-563): 'idle' means opacity: 0, which is only
        // valid while the header's natural position is offscreen. Spurious
        // scroll events near rest (iOS 26 momentum settle/bounce) can park it
        // 'idle' while still in view, where the !#offscreen early return in
        // #updateScrollState keeps it invisible until refresh. Intersecting +
        // idle is never a legitimate state — reset it.
        if (isIntersecting && this.dataset.stickyState === 'idle') {
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        }
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached, event.detail.requiredExtraWidth);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   * @param {number} [requiredExtraWidth] - TS - Custom override (TS-493): the reflow's reported deficit — how much wider the list must grow for every link to fit
   */
  #updateMenuVisibility(hideMenu, requiredExtraWidth = 0) {
    if (hideMenu) {
      // TS - Custom override (TS-493): the reflow reports how much wider the list must
      // grow for every link to fit; the layout viewport and the list grow 1:1, so
      // clientWidth + deficit is the exact recovery width (+1 for rounding). Without a
      // usable deficit, fall back to retrying on any growth (probing).
      const deficit =
        Number.isFinite(requiredExtraWidth) && requiredExtraWidth > 0 ? Math.ceil(requiredExtraWidth) + 1 : 1;
      this.#menuRecoveryWidth = document.documentElement.clientWidth + deficit;

      // TS - Custom override (TS-493): remember the widest collapse width and the
      // recovery width, so the pre-paint script in theme.liquid can paint the drawer
      // up front on the next page view instead of snapping from the desktop layout,
      // and connectedCallback can seed a flicker-free recovery point. Keyed by the
      // menu-contents signature so menu edits invalidate the cache. Uses the layout
      // viewport (clientWidth, excludes the scrollbar) — the nav's fit is governed by
      // layout width, so a collapse recorded on a scrolling page can't force drawer
      // mode on a scrollbar-less page where the nav fits.
      try {
        const headerMenu = /** @type {HTMLElement | null} */ (this.querySelector('header-menu'));
        const signature = headerMenu?.dataset.menuSignature ?? '';
        const [storedWidth, , storedSignature] = (sessionStorage.getItem('ts-header-collapse') ?? '').split(':');
        const width = Math.max(
          document.documentElement.clientWidth,
          storedSignature === signature ? Number(storedWidth) || 0 : 0
        );
        sessionStorage.setItem('ts-header-collapse', `${width}:${this.#menuRecoveryWidth}:${signature}`);
      } catch {
        // Storage unavailable (e.g. blocked) — the fast path is a progressive enhancement.
      }
    } else {
      this.#menuRecoveryWidth = null;
      // TS - Custom override (TS-493): while the menu is hidden its overflow-list reflows with
      // zero rects and never clears `minimum-reached`, so setHeaderMenuStyle would read the stale
      // attribute and stay in drawer mode. Clear it here; if the links still don't fit, the
      // reflow after the menu becomes visible re-sets it and re-collapses.
      this.querySelector('overflow-list')?.removeAttribute('minimum-reached');

      // TS - Custom override (TS-493): if the drawer is open while we switch back to the
      // inline menu, close it properly — otherwise CSS hides it with its scroll lock
      // (html[scroll-lock]) still engaged, leaving the page unscrollable with no visible
      // way out. close() resolves immediately when the hidden drawer has no animations.
      const drawer = /** @type {(HTMLElement & { isOpen?: boolean, close?: () => void }) | null} */ (
        this.querySelector('header-drawer')
      );
      if (drawer?.isOpen && typeof drawer.close === 'function') {
        drawer.close();
      }
    }
    setHeaderMenuStyle();
  }

  /**
   * TS - Custom override (TS-577): flips 'hiding' to 'idle' once the fade-out
   * has finished, releasing the sticky pin only when the header is invisible.
   */
  #scheduleHidingRelease() {
    this.#timeout = window.setTimeout(() => {
      this.#timeout = null;
      if (this.dataset.stickyState !== 'hiding') return;
      this.dataset.stickyState = 'idle';
    }, HeaderComponent.#HIDE_FADE_MS);
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    // TS - Custom override (TS-522): while the menu drawer is open the page is
    // scroll-locked, so any scroll event is an artifact of a dialog's <body>
    // lock/unlock (bag, search). Reacting would flip the sticky header to
    // 'idle' (opacity: 0) and hide the open drawer — which keeps intercepting
    // every tap, freezing the viewport.
    const drawer = /** @type {(HTMLElement & { isOpen?: boolean }) | null} */ (this.querySelector('header-drawer'));
    if (drawer?.isOpen) return;

    // TS - Custom override (TS-563): rubber-band overscroll reports scrollTop
    // beyond the document's range at both edges — top bounce-back (-30 → 0)
    // reads as scroll-down, bottom bounce-back (max+30 → max) as scroll-up.
    // Clamp to the scrollable range so overscroll never registers as intent.
    const scrollingElement = document.scrollingElement;
    const maxScrollTop = scrollingElement ? scrollingElement.scrollHeight - scrollingElement.clientHeight : 0;
    const scrollTop = Math.min(Math.max(0, scrollingElement?.scrollTop ?? 0), Math.max(0, maxScrollTop));

    // TS - Custom override (TS-516): a dialog-component scroll lock puts <body>
    // in position: fixed, collapsing the document's scroll height — scrollTop
    // snaps to 0 and fires a scroll event that reads as scroll-up, re-expanding
    // the collapsed header under the open modal (e.g. Quick View on a
    // collection page). That event can arrive long after dialog:open (content
    // injection blocks the next paint), so a suppression window isn't enough —
    // detect the lock itself, which is still engaged when the event runs.
    if (document.body.style.position === 'fixed') {
      this.#lastScrollTop = scrollTop;
      return;
    }

    // TS - Custom override (TS-504): scroll-anchoring adjustments from an
    // animating accordion read as scroll-up and would flash the header into
    // view (shifting the sticky add-to-cart bar with it). Skip the direction
    // logic but keep the baseline current, so the first real scroll after the
    // window compares against fresh state.
    if (performance.now() < this.#anchorSuppressedUntil) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    const headerRect = this.getBoundingClientRect();
    const headerTop = headerRect.top;
    // TS - Custom override (TS-563): position: sticky only applies while
    // 'active' or 'hiding' (sections/header.liquid), so whenever the header
    // isn't pinned the rect reflects its true natural position — cache the
    // slot bottom in document space for the in-view guard below. Caching
    // while pinned would poison the guard with the pinned rect (TS-577).
    if (this.dataset.stickyState !== 'active' && this.dataset.stickyState !== 'hiding') {
      this.#naturalSlotBottom = scrollTop + headerRect.bottom;
    }
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    // TS - Custom override (TS-563): while 'active' the header sticks at
    // top: -1px, so a scroll-up that settles fractionally above 0 leaves
    // headerTop just under 0 — treat anything within the pin depth as top.
    const isAtTop = headerTop >= 0 || scrollTop <= HeaderComponent.#REST_TOLERANCE_PX;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (isAtTop || scrollTop < (this.#naturalSlotBottom || headerRect.height)) {
      // TS - Custom override (TS-563): a not-up read (duplicate or
      // fractionally increasing scrollTop from momentum settle) must never
      // set 'idle' while the header's natural slot is within the viewport
      // (scrollTop < measured slot bottom; height as fallback until first
      // measure) — that fades out a header the resting page would show, and
      // since it never fully exits the viewport, no intersection change
      // ever fires to heal it. Render it at rest instead.
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
    } else if (scrollTop <= this.#lastScrollTop + HeaderComponent.#REST_TOLERANCE_PX) {
      // TS - Custom override (TS-563): a coalesced duplicate or sub-1px settle
      // read after an upward fling is not downward intent — hiding here fades
      // the just-revealed header the moment scrolling stops. Keep the state
      // AND the baseline (no #lastScrollTop update), so genuine slow scrolling
      // accumulates past the tolerance and still hides normally.
      // TS - Custom override (TS-577): a fling-down's settle tail lands here
      // too — the clearTimeout above ran, so re-arm the pending release or
      // these reads strand the header in 'hiding' (pinned but invisible).
      if (this.dataset.stickyState === 'hiding') this.#scheduleHidingRelease();
      return;
    } else if (
      this.dataset.stickyState === 'active' &&
      scrollTop <= this.#lastScrollTop + HeaderComponent.#HIDE_HYSTERESIS_PX
    ) {
      // TS - Custom override (TS-577): the revealed header only hides after
      // real downward travel — small down-reads (iOS 26 settle jitter) hold
      // both the state and the baseline, like the rest-tolerance branch above.
      return;
    } else if (this.dataset.stickyState === 'hiding') {
      // TS - Custom override (TS-577): fade in progress — re-arm the release
      // (the clearTimeout above ran) so the pin always lets go after the fade.
      this.#scheduleHidingRelease();
    } else if (this.dataset.stickyState === 'active') {
      // TS - Custom override (TS-577): two-phase hide. Dropping straight to
      // 'idle' releases position: sticky while the header is still fully
      // opaque — it vanishes in a single frame, flashing the content
      // underneath. 'hiding' keeps the pin while the opacity fades, then the
      // release hands over to 'idle'.
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'hiding';
      this.#scheduleHidingRelease();
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // TS - Custom override (TS-493): when the pre-paint script in theme.liquid started
    // us in drawer mode from the stored collapse width, no overflow event fires (the
    // hidden menu can't be measured) — seed the recovery width from the stored value
    // so the menu returns flicker-free at the width it last fit, and set
    // minimum-reached so every other setHeaderMenuStyle() caller (header-menu.js
    // resize listener, theme editor) sees a consistent drawer state instead of
    // flipping back to the desktop layout. #updateMenuVisibility(false) removes the
    // attribute, so recovery is unaffected.
    if (this.dataset.menuStyle === 'drawer' && this.#menuRecoveryWidth === null && !isTouchDevice()) {
      let storedRecoveryWidth = 0;
      try {
        storedRecoveryWidth = Number((sessionStorage.getItem('ts-header-collapse') ?? '').split(':')[1]) || 0;
      } catch {
        // Storage unavailable — fall back to probing on any growth.
      }
      this.#menuRecoveryWidth = Math.max(storedRecoveryWidth, document.documentElement.clientWidth + 1);
      this.querySelector('overflow-list')?.setAttribute('minimum-reached', '');
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
        // TS - Custom override (TS-504)
        document.addEventListener('toggle', this.#handleDetailsToggle, true);
        document.addEventListener('wheel', this.#handleScrollIntent, { passive: true });
        document.addEventListener('touchmove', this.#handleScrollIntent, { passive: true });
        document.addEventListener('keydown', this.#handleScrollIntent);
        // TS - Custom override (TS-516)
        document.addEventListener('dialog:open', this.#handleDialogToggle, true);
        document.addEventListener('dialog:close', this.#handleDialogToggle, true);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    // TS - Custom override (TS-504)
    document.removeEventListener('toggle', this.#handleDetailsToggle, true);
    document.removeEventListener('wheel', this.#handleScrollIntent);
    document.removeEventListener('touchmove', this.#handleScrollIntent);
    document.removeEventListener('keydown', this.#handleScrollIntent);
    // TS - Custom override (TS-516)
    document.removeEventListener('dialog:open', this.#handleDialogToggle, true);
    document.removeEventListener('dialog:close', this.#handleDialogToggle, true);
    // TS - Custom override (TS-493)
    if (this.#menuRetryTimeout) {
      clearTimeout(this.#menuRetryTimeout);
      this.#menuRetryTimeout = null;
    }
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
